import { first, isArray } from 'lodash';

export default {
    data: () => ({
        validationErrors: {},
    }),

    methods: {
        /**
         * Clears validation errors.
         */
        clearValidationErrors() {
            this.validationErrors = {};
        },

        /**
         * Clears validation error.
         *
         * @param  {string} field Field name.
         */
        clearValidationError(field) {
            this.$delete(this.validationErrors, field);
        },

        /**
         * Gets the validation error.
         *
         * @param   {string} field     Field name.
         * @returns {string|undefined} Field error.
         */
        getValidationError(field) {
            return this.validationErrors[field];
        },

        /**
         * Checks whether given field has validation error.
         *
         * @param   {string}  field Field name.
         * @returns {boolean}       Check result.
         */
        hasValidationError(field) {
            return !! this.getValidationError(field);
        },

        /**
         * Checks whether there are any validation errors.
         *
         * @returns {boolean} Check result.
         */
        hasValidationErrors() {
            return 0 < Object.values(this.validationErrors).length;
        },

        /**
         * Validates form.
         *
         * @param  {object.<string, any>} data       Form data
         * @param  {object.<string, Function[]|Function>} validators Form validators.
         */
        async validateForm(data, validators) {
            for (const field in validators) {
                const value = data[field];
                const validator = validators[field];

                const error = await this.validateField(field, value, validator, data);

                if (error) {
                    this.$set(this.validationErrors, field, error);
                }
            }
        },

        /**
         * Validates field against validators.
         *
         * @param  {string}              field      Field name.
         * @param  {any}                 value      Field value.
         * @param  {Function[]|Function} validators List of validators.
         * @param  {object}              data       Field values.
         */
        async validateField(field, value, validators, data) {
            if (! Array.isArray(validators)) {
                validators = [validators];
            }

            for (const validator of validators) {
                if (! (validator instanceof Function)) {
                    continue;
                }

                const error = await validator(value, { field, data });

                if (error) {
                    return error;
                }
            }
        },

        /**
         * Populates errors from response.
         *
         * @param  {object} errors         Errors list.
         * @param  {object} fieldResolver  Field name resolver.
         */
        populateValidationErrors(errors, fieldResolver = null) {
            for (const _field in errors) {
                const error = errors[_field];
                const field = fieldResolver instanceof Function
                    ? fieldResolver(_field)
                    : _field;

                if (this.hasValidationError(field)) {
                    continue;
                }

                this.$set(
                    this.validationErrors,
                    field,
                    isArray(error) ? first(error) : error
                );
            }
        },
    },
};
